import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';

import { getCurrentUserProfile } from '../../../api/identity-data-hub/getCurrentUserProfile';
import { PostContactFormDto } from '../../../api/simple-project-service/project-service.types';
import {
  ContactForm,
  ContactFormData,
  ContactFormValues,
  initialContactFormData,
} from '../../../components/ContactForm/ContactForm';
import {
  getInitialPublicContactFormData,
  PublicContactForm,
  PublicContactFormData,
  PublicContactFormValues,
} from '../../../components/ContactForm/PublicContactForm';
import { useApp } from '../../../hooks/useApp';
import { useAsync } from '../../../hooks/useAsync';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { SESSION_KEY, useSessionStorage } from '../../../hooks/useSessionStorage';
import { useLocalisation } from '../../../services/localisationContext';
import { getWindow } from '../../../services/windowService';

interface Props {
  onClose: () => void;
  onRequest: (values: PostContactFormDto) => void;
}

export const RequestModalForms: React.FC<Props> = ({ onClose, onRequest }) => {
  const { loggedIn: isLoggedIn } = useApp();
  const { configurationId: uuidFromUrl } = useParams<HasConfigurationId>();
  const tContactForm = useTranslate('ContactForm');
  const { currentLanguage } = useLocalisation();
  const [contactFormData, setContactFormData] = useState<ContactFormData>();
  const [publicContactFormData, setPublicContactFormData] = useState<PublicContactFormData>();
  const [lastUsedPublicFormData] = useSessionStorage<PublicContactFormValues>(
    SESSION_KEY.CONFIGURATOR_PUBLIC_CONTACT_FORM_VALUES,
  );
  const [lastUsedFormData] = useSessionStorage<PublicContactFormValues>(
    SESSION_KEY.CONFIGURATOR_CONTACT_FORM_VALUES,
  );
  const [selectedCountry] = useLocalStorage<string>('selectedCountry');

  const { start: loadCurrentUserProfile, result: currentUserProfile } =
    useAsync(getCurrentUserProfile);

  useEffect(() => {
    setPublicContactFormData(
      getInitialPublicContactFormData(tContactForm, lastUsedPublicFormData, selectedCountry),
    );
  }, [tContactForm, lastUsedPublicFormData, selectedCountry]);

  useEffect(() => {
    setContactFormData(initialContactFormData(tContactForm, selectedCountry, lastUsedFormData));
  }, [tContactForm, lastUsedFormData, selectedCountry]);

  useEffect(() => {
    if (isLoggedIn && !currentUserProfile) {
      loadCurrentUserProfile();
    }
  }, [isLoggedIn, currentUserProfile, loadCurrentUserProfile]);

  useEffect(() => {
    if (!currentUserProfile) {
      return;
    }
    const partial: Partial<ContactFormValues> = {
      firstName: currentUserProfile.firstName,
      lastName: currentUserProfile.lastName,
      email: currentUserProfile.eMail,
      phone: currentUserProfile.landline || currentUserProfile.mobile || '',
    };
    setContactFormData((current) => ({
      ...current,
      values: {
        ...current.values,
        ...partial,
        country: selectedCountry,
      },
    }));
  }, [currentUserProfile, selectedCountry]);

  const handleSubmitContactForm = useCallback(
    (values: ContactFormValues) => {
      const [configurationUrl] = getWindow().location.href.split(/[?#]/);
      const payload: PostContactFormDto & { phone?: string } = {
        ...values,
        businessPhone: '',
        mobilePhone: '',
        phone: undefined,
        name: null,
        language: currentLanguage,
        isPrivacyAccepted: true,
        countryName: tContactForm(values.country),
        parameters: {
          configurationUrl,
          configurationId: uuidFromUrl,
          phone: values.phone,
        },
      };
      onRequest(payload);
    },
    [currentLanguage, onRequest, tContactForm, uuidFromUrl],
  );

  return (
    <>
      {isLoggedIn && contactFormData && (
        <ContactForm
          formData={contactFormData}
          onSubmit={handleSubmitContactForm}
          onClose={onClose}
        />
      )}
      {!isLoggedIn && publicContactFormData && (
        <PublicContactForm
          formData={publicContactFormData}
          onSubmit={handleSubmitContactForm}
          onClose={onClose}
        />
      )}
    </>
  );
};

type HasConfigurationId = { configurationId: string };
